// Copyright: IQGeo Limited 2010-2019
import $ from 'jquery';
import _ from 'underscore';
import myw from 'myWorld-base';
import L from 'leaflet';
import 'L.Control.MiniMap';

/**
 * Options for {@link MinimapPlugin}
 * @typedef minimapOptions
 * @property {boolean} enabled          If it is set to false, the minimap wont be initialized or shown on the map
 * @property {string}  position         The standard Leaflet.Control position parameter
 * @property {integer} width            The width of the minimap in pixels
 * @property {integer} height           The height of the minimap in pixels
 * @property {integer} zoomLevelOffset  The offset applied to the zoom in the minimap compared to the zoom of the main map. Can be positive or negative
 * @property {boolean} minimized        Governs if the minimap will be minimized when you first load the app
 * @property {Array<string>} excludedLayerClasses   List of classes that don't work well as an overview map
 */

myw.MinimapPlugin = myw.Plugin.extend({
    // Default config options for minimap plugin
    options: {
        enabled: true, // If false, the minimap won't be initialized or shown on the map
        position: 'bottomright', //The standard Leaflet.Control position parameter
        width: 150, // The width of the minimap in pixels
        height: 150, // The height of the minimap in pixels
        zoomLevelOffset: -5, // The offset applied to the zoom in the minimap compared to the zoom of the main map. Can be positive or negative
        minimized: false, // Governs if the minimap will be minimized when you first load the app
        excludedLayerClasses: ['myw.BlankLayer', 'myw.MywVectorLayer']
    },

    /**
     * Initializes the plugin and sets its event handlers
     * @class  A plugin to display the overview map using leaflet's minimap control. <br/>
     * Listens for the baselayerchange event and updates the minimap control with the current baselayer. <br/>
     * Minimap view options can be customized on a sytem level using the config settings and on application level in the application class
     * @param  {Object}         owner   The application
     * @param  {minimapOptions} options Configuration options for the minimap plugin
     * @constructs
     * @extends {Plugin}
     */
    initialize(owner, options) {
        myw.Plugin.prototype.initialize.call(this, owner, options);
        this.setOptions(myw.config['core.plugin.minimap']); // Merges in the options from config settings

        this.layers = [];
        this.map = this.app.map;

        if (this.options.enabled && !this.app.isHandheld) {
            this.enable();
        } else {
            this.enabled = false;
        }
    },

    /**
     * Adds the minimap control to the application's geographical map
     */
    enable() {
        this.enabled = true;
        Promise.all([myw.Util.loadStyleSheet('lib/leaflet/plugins/Control.MiniMap.css')]).then(
            () => {
                const basemapName = this.map.getCurrentBaseMapName();
                if (basemapName) this.updateMinimapForLayer(basemapName);
                this.initiateEventHandlers();
            }
        );
    },

    initiateEventHandlers() {
        this.map.on('baselayerchange', this._updateMinimapForLayerChange, this);

        this.app.on('new-map', event => {
            if (this.minimap) {
                //remove handler on old map
                this.map.off('baselayerchange', this._updateMinimapForLayerChange, this);
                this.minimap = null; //will be recreated
                //set handler on new map
                event.map.on('baselayerchange', this._updateMinimapForLayerChange, this);
            }
        });
    },

    /**
     * Removes the minimap control from the application's geographical map
     */
    disable() {
        this.enabled = false;
        this.map.off('baselayerchange', this._updateMinimapForLayerChange, this);
        if (this.minimap) this.minimap.remove();
        this.minimap = null; //will be recreated
    },

    _updateMinimapForLayerChange(event) {
        return this.updateMinimapForLayer(event.layer.display_name);
    },

    /**
     * Adds a minimap control for the layer, if a minimap already exists, it updates its layer
     * @param {string}         layerName Name of the layer to be shown in the minimap view
     */
    async updateMinimapForLayer(layerName) {
        const layersDefs = await this.app.getLayersDefs();
        const layerDef = _.findWhere(layersDefs, { category: 'basemap', display_name: layerName });
        const datasource = this.app.database.getDatasource(layerDef.datasource);
        const layer = new myw.Layer(datasource, layerDef);
        let isIncompatible;
        isIncompatible =
            layer &&
            _.any(this.options.excludedLayerClasses, excLayerClassExpr => {
                const excLayerClass = myw.Util.evalAccessors(excLayerClassExpr);
                return excLayerClass && layer instanceof excLayerClass;
            });

        if (isIncompatible) {
            if (this.minimap) $(this.minimap._container).hide();
        } else if (this.minimap) {
            //update existing minimap
            $(this.minimap._container).show();
            this.minimap.changeLayer(layer);
        } else {
            this.createMinimap(layer);
        }
    },

    /**
     * Creates an instance of the minimap with the layer supplied to it.
     * @param {L.ILayer} layer Leaflet layer to be shown in the minimap
     */
    createMinimap(layer) {
        this.minimap = new L.Control.MiniMap(
            layer,
            {
                toggleDisplay: true,
                width: this.options.width,
                height: this.options.height,
                zoomLevelOffset: this.options.zoomLevelOffset,
                // Workaround to problem on tablet with zoomAnimation off and user attempts to pinch zoom.
                zoomAnimation: L.Browser.touch,
                strings: {
                    hideText: this.msg('hide_tooltip'),
                    showText: this.msg('show_tooltip')
                }
            },
            {
                zoomsliderControl: false
            }
        );
        this.minimap.addTo(this.app.map);
        this.minimap._setDisplay(this.options.minimized);
        this.minimap._miniMapMoving = false;
    },
    /**
     * Returns state to be included in map link denoting the display state of the minimap
     * @return {string}   "close" if its minimized, "open" if its not minimized
     */
    getStateForAppLink() {
        if (this.minimap) {
            const displayState = this.minimap._minimized ? 'close' : 'open';
            return displayState;
        }
    },

    /**
     * Called by the application on startup if there has been an url parameter specified for self.
     * @param  {string}     param   Value of the url parameter for this plugin.
     *                              If 'close', minimap will be minimized
     */
    setStateFromAppLink(param) {
        const minimized = param === 'close';
        this.options.minimized = minimized;
    },

    /**
     * Sets the current state of the plugin
     * @param {object} state
     * @param {boolean} state.minimized Whether the minimap is minimized or not
     */
    setState(state) {
        if (this.minimap) this.minimap._setDisplay(state.minimized);
        else this.options.minimized = state.minimized;
    },

    /**
     * Returns object with current state details <br/>
     * @return {object}
     * @param {boolean} state.minimized Whether the minimap is minimized or not
     */
    getState() {
        return {
            enabled: this.enabled,
            minimized: this.minimap && this.minimap._minimized
        };
    },

    /**
     * Toggles enabled state of the plugin.
     * If the minimap control is on the map, removes it.
     * If the minimap control is not on the map, adds it
     * @return {boolean} Whether the control is now enabled or not
     */
    toggle() {
        const wasEnabled = this.enabled;
        if (wasEnabled) {
            this.disable();
        } else {
            this.options.minimized = false;
            this.enable();
        }
        return !wasEnabled;
    },

    /**
     * Actions to be used by map context menu
     */
    actions: {
        toggle: {
            action: 'toggle',
            checked: 'enabled'
        }
    }
});

export default myw.MinimapPlugin;
